/**
 * Exports — ADMIN only (8 Sep 2026).
 *
 * Five reports, each downloadable as a spreadsheet or a PDF, with the first
 * rows shown on the screen first.
 *
 * ---------------------------------------------------------------------------
 * Why this is a screen of its own
 * ---------------------------------------------------------------------------
 * It sat on Data tools for a day and does not belong there. That screen exists
 * to hold the operation that erases the program data, and its whole design —
 * a deliberate navigation step, a typed confirmation, warnings about what
 * cannot be undone — is built around a control nobody should reach by accident.
 * Downloading a list is the opposite kind of act: routine, non-destructive, and
 * something a coordinator may want weekly.
 *
 * ---------------------------------------------------------------------------
 * Why admin-only, when nothing here changes data
 * ---------------------------------------------------------------------------
 * Because of what the files hold rather than what they do. The volunteer export
 * is the entire directory — every name, email, phone number and home city — in
 * a file that leaves the portal and its audit trail permanently and cannot be
 * recalled. The guard on the API route is what enforces that; this page only
 * decides what is offered. The preview is guarded the same way and for the same
 * reason: a preview of the directory is still the directory.
 *
 * ---------------------------------------------------------------------------
 * The preview (Parinaam, 12 Oct 2026)
 * ---------------------------------------------------------------------------
 * The screen used to offer five downloads and say nothing about what was in
 * them beyond a sentence. The only way to find out whether a date range had
 * caught the right rows was to download the file and open it, and the only way
 * to correct a mistake was to do that again.
 *
 * So one report is chosen at a time and its first rows are shown. What makes
 * this worth trusting is that the preview comes from the same query as the
 * file — same window, same columns, same order — rather than from a lighter
 * one built to look similar. See `preview` in exports.service.ts.
 *
 * The download goes through `apiDownload` rather than an `<a href>`, because
 * the routes need the bearer token and a link element sends no headers.
 */

import { useState } from 'react';
import {
  Button,
  Card,
  DateRangePicker,
  EmptyState,
  ErrorState,
  LoadingState,
  PageHeader,
} from '../../components/common';
import type { DateRange } from '../../components/forms/DateRangePicker';
import { useToast } from '../../components/common/Toast';
import { useAuth } from '../../hooks/useAuth';
import { useExportPreview } from '../../hooks/useExports';
import { apiDownload } from '../../services/api/client';
import { exportPath } from '../../services/api/exports';
import type { ExportDataset, ExportFormat, PaperChoice } from '../../services/api/exports';
import styles from './ExportsPage.module.css';

interface ExportOption {
  dataset: ExportDataset;
  label: string;
  /** What is actually in the file — the columns nobody can guess from a title. */
  describes: string;
  /**
   * Which date the window narrows this file on.
   *
   * Spelled out per file because the five differ and the difference matters:
   * the directory filters on when somebody was ADDED, which is not the same
   * list as who was active.
   */
  datesOn: string;
}

const EXPORTS: ExportOption[] = [
  {
    dataset: 'consolidated',
    label: 'Consolidated report',
    describes: 'Everything: programme, session, volunteer, hours.',
    datesOn: 'the session’s dates',
  },
  {
    dataset: 'programs',
    label: 'Programs',
    describes: 'Every programme, with status and dates.',
    datesOn: 'the programme’s dates',
  },
  {
    dataset: 'activities',
    label: 'Activities',
    describes: 'Every activity, with turnout and hours.',
    datesOn: 'the activity’s dates',
  },
  {
    dataset: 'volunteers',
    label: 'Volunteers',
    describes: 'The whole directory, with hours given.',
    datesOn: 'when the volunteer was added',
  },
  {
    dataset: 'volunteer-activities',
    label: 'Volunteers × activities',
    describes: 'One row per volunteer per session.',
    datesOn: 'the session’s dates',
  },
];

const FORMATS: { format: ExportFormat; label: string }[] = [
  { format: 'xlsx', label: 'Excel' },
  { format: 'pdf', label: 'PDF' },
];

/**
 * What sheet the PDF will use, and why.
 *
 * The "why" is the whole point. "A0" on its own reads as a mistake; "37
 * columns need 2,869pt of width, and A0 is the smallest standard sheet they
 * fit on" reads as an answer. It is a tooltip rather than a line of text
 * because it is reference material — wanted once, not on every visit.
 *
 * Hover AND focus, with `tabIndex`, so it is reachable from the keyboard.
 * A `title` attribute would have been one line, but it never appears for
 * somebody who is not using a mouse.
 */
function PaperBadge({ paper }: { paper: PaperChoice }) {
  /*
   * Three outcomes, three sentences. The middle one is easy to miss and was:
   * a table can be made to sit upright by trimming its widest columns, and
   * without saying so the tooltip claimed 569pt fits in 539pt.
   */
  const ending = paper.split
    ? 'still not enough, so the columns are split across pages and the first is repeated.'
    : paper.drawnWidth < paper.tableWidth
      ? 'so the widest columns are trimmed to keep it upright.'
      : 'the smallest standard sheet they fit on.';

  const tip =
    `${paper.tableWidth.toLocaleString()}pt of columns. ` +
    `${paper.label} ${paper.orientation} gives ${paper.sheetWidth.toLocaleString()}pt — ` +
    ending;

  return (
    <span className={styles.paper} tabIndex={0} aria-describedby="paper-why">
      <span className={styles.paperLabel}>PDF paper</span>
      <span className={styles.paperValue}>
        {paper.label} {paper.orientation}
      </span>
      <span className={styles.paperTip} id="paper-why" role="tooltip">
        {tip}
      </span>
    </span>
  );
}

export function ExportsPage() {
  const { hasRole } = useAuth();
  const toast = useToast();

  const [chosen, setChosen] = useState<ExportDataset>('consolidated');
  /** Which format is in flight, so only its own button spins. */
  const [downloading, setDownloading] = useState<ExportFormat | null>(null);

  /*
   * All, or a window. Two pieces of state rather than one, deliberately.
   *
   * Switching to All and back should not make somebody retype the dates they
   * just chose, so the range is remembered while All is selected and simply not
   * sent. Deriving "is this All?" from an empty range instead would make the
   * two inseparable: clearing one date would silently become a complete export,
   * which is the opposite of what somebody mid-edit meant.
   */
  const [scope, setScope] = useState<'all' | 'range'>('all');
  const [range, setRange] = useState<DateRange>({ from: '', to: '' });

  const windowed = scope === 'range' && Boolean(range.from || range.to);
  // All sends no parameters at all, rather than an empty pair — a complete
  // export stays the request this endpoint has always taken.
  const query = windowed ? { from: range.from, to: range.to } : {};

  const admin = hasRole('ADMIN');
  const option = EXPORTS.find((item) => item.dataset === chosen) as ExportOption;
  // Not for a TEAM account: the preview returns the same rows as the file, and
  // a hook runs before the check below can return.
  const preview = useExportPreview(chosen, query, admin);

  if (!admin) {
    return (
      <>
        <PageHeader title="Export" />
        <Card>
          <EmptyState title="Admins only" message="These files carry personal contact details." />
        </Card>
      </>
    );
  }

  async function download(format: ExportFormat) {
    setDownloading(format);
    try {
      await apiDownload(
        exportPath(chosen, { ...query, format }),
        `parinaam-${chosen}.${format}`
      );
    } catch (error) {
      toast.error(error, `The ${option.label.toLowerCase()} export could not be downloaded.`);
    } finally {
      setDownloading(null);
    }
  }

  return (
    <>
      <PageHeader title="Export" description="Check a report, then download it." />

      <Card title="Report">
        <ul className={styles.reports}>
          {EXPORTS.map((item) => (
            <li key={item.dataset}>
              <label
                className={`${styles.report} ${item.dataset === chosen ? styles.reportOn : ''}`}
              >
                <input
                  type="radio"
                  name="export-report"
                  checked={item.dataset === chosen}
                  onChange={() => setChosen(item.dataset)}
                />
                <span>
                  <span className={styles.reportLabel}>{item.label}</span>
                  <span className={styles.reportDescribes}>{item.describes}</span>
                </span>
              </label>
            </li>
          ))}
        </ul>
      </Card>

      <Card title="Dates">
        <fieldset className={styles.scope}>
          <legend className={styles.srOnly}>Dates</legend>

          <label className={styles.radio}>
            <input
              type="radio"
              name="export-scope"
              checked={scope === 'all'}
              onChange={() => setScope('all')}
            />
            <span>
              <strong>All</strong> — the complete export, nothing filtered out.
            </span>
          </label>

          <label className={styles.radio}>
            <input
              type="radio"
              name="export-scope"
              checked={scope === 'range'}
              onChange={() => setScope('range')}
            />
            <span>
              <strong>A date range</strong> — only what falls inside it.
            </span>
          </label>

          {scope === 'range' && (
            <div className={styles.rangeBox}>
              <DateRangePicker value={range} onChange={setRange} label="Date range for the export" />
              {!windowed && (
                <p className={styles.rangeHint}>Both ends empty downloads everything.</p>
              )}
            </div>
          )}
        </fieldset>

        {windowed && <p className={styles.exportDates}>Dates filter on {option.datesOn}.</p>}
      </Card>

      <Card
        title={option.label}
        action={
          <div className={styles.actions}>
            {/*
              Read-only, and beside the PDF button because it describes that
              one download. It was a dropdown for an afternoon; the question it
              asked has one right answer that the reader cannot compute, so it
              is measured and reported instead.
            */}
            {preview.data && <PaperBadge paper={preview.data.paper} />}

            {FORMATS.map(({ format, label }) => (
              <Button
                key={format}
                variant={format === 'xlsx' ? 'primary' : 'secondary'}
                loading={downloading === format}
                /*
                  Both buttons while one runs: two downloads at once is never
                  what somebody meant, and the second would race the first's
                  anchor. NOT while the preview loads, though — the server does
                  its own query, and disabling here meant changing a date and
                  then clicking Download did nothing at all.
                */
                disabled={downloading !== null}
                onClick={() => void download(format)}
              >
                {label}
              </Button>
            ))}
          </div>
        }
      >
        {preview.isPending ? (
          <LoadingState label="Reading the report…" />
        ) : preview.isError ? (
          <ErrorState error={preview.error} onRetry={() => void preview.refetch()} />
        ) : (
          <>
            <p className={styles.previewNote}>
              {preview.data.total === 0
                ? 'Nothing matches — the file would hold its headings and no rows.'
                : `Showing the first ${preview.data.shown} of ${preview.data.total} rows · ${preview.data.columns.length} columns`}
            </p>

            {/*
              Its own scroll box, so a 37-column report never makes the page
              itself scroll sideways. The same rule the data tables follow.
            */}
            <div className={styles.previewScroll}>
              <table className={styles.preview}>
                <thead>
                  <tr>
                    {preview.data.columns.map((column) => (
                      <th key={column} scope="col">
                        {column}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {preview.data.rows.map((row, index) => (
                    // The index is the key because a row here has no identity
                    // of its own — this is a rendering of a file, not a list of
                    // records, and nothing reorders.
                    <tr key={index}>
                      {row.map((value, column) => (
                        <td key={column}>{value === null ? '' : String(value)}</td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {preview.data.total > preview.data.shown && (
              <p className={styles.previewNote}>
                The download holds all {preview.data.total} rows.
              </p>
            )}
          </>
        )}
      </Card>

      <p className={styles.caution}>These files carry personal contact details.</p>
    </>
  );
}
